/**
 * Customer self-registration.
 *
 * Reached after the phone number is verified but no account exists. Captures
 * the full field set from the requirements, then waits for admin approval —
 * catalogue and ordering stay locked until then.
 *
 * The GPS pin is captured here and stored on the shop. Every later map link in
 * the app is a deep link built from those coordinates, so no Maps API is called
 * and there is no per-view cost.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Banner, Button, Card, Select, Switch, TextArea, TextInput, useToast } from '../../components/ui';
import { getCoords } from '../../lib/camera';
import { IconPin } from '../../components/Icons';
import { phoneInputDigits } from '../../lib/phone';
import StaffRegister from './StaffRegister';

interface Ref { id: string; code: string; name_en: string }

export default function Register() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const phone = (location.state as { phone?: string } | null)?.phone ?? '';
  /**
   * Proof that this number was verified by OTP, handed over by the login
   * screen. The register endpoint will not accept a self-registration
   * without it — see the note on the ticket in api/src/routes/customers.ts.
   */
  const ticket = (location.state as { ticket?: string } | null)?.ticket;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  /**
   * Who is registering. A verified-but-unknown number could be a new shop, a
   * salesman or a driver — the one screen cannot guess, so it asks. Shops fall
   * through to the form below; staff go to their own enrolment form, which
   * submits to the admin approval queue with no access until approved.
   */
  const [who, setWho] = useState<'' | 'shop' | 'salesperson' | 'driver'>('');

  const [areas, setAreas] = useState<Ref[]>([]);
  const [storeTypes, setStoreTypes] = useState<Ref[]>([]);

  // Business
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [pocName, setPocName] = useState('');
  const [altPhone, setAltPhone] = useState('');
  const [gstin, setGstin] = useState('');
  const [fssai, setFssai] = useState('');
  const [salespersonCode, setSalespersonCode] = useState('');

  // Shop
  const [shopName, setShopName] = useState('');
  const [storeTypeId, setStoreTypeId] = useState('');
  const [address1, setAddress1] = useState('');
  const [landmark, setLandmark] = useState('');
  const [areaId, setAreaId] = useState('');
  const [pincode, setPincode] = useState('');
  const [openTime, setOpenTime] = useState('09:00');
  const [closeTime, setCloseTime] = useState('21:00');
  const [open24, setOpen24] = useState(false);
  const [weeklyOff, setWeeklyOff] = useState('');

  // Location
  const [coords, setCoords] = useState<{ latitude: number; longitude: number; accuracyM: number } | null>(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    // Reference data is public enough to fetch before sign-in completes; these
    // endpoints only return codes and names.
    void (async () => {
      try {
        const [a, s] = await Promise.all([
          api.get<{ type: string; items: Ref[] }>('/api/admin/masters/areas').catch(() => ({ items: [] as Ref[] })),
          api.get<{ type: string; items: Ref[] }>('/api/admin/masters/store-types').catch(() => ({ items: [] as Ref[] })),
        ]);
        setAreas(a.items ?? []);
        setStoreTypes(s.items ?? []);
      } catch { /* the form still works with free-text address */ }
    })();
  }, []);

  async function captureLocation() {
    setLocating(true);
    const c = await getCoords({ highAccuracy: true });
    setLocating(false);
    if (!c) {
      toast.show('Could not get your location. You can still continue.', 'danger');
      return;
    }
    setCoords(c);
  }

  /**
   * Consent state.
   *
   * `agreed` covers the four the service cannot run without, as ONE tick
   * against one sentence — four separate mandatory boxes is a consent form
   * that teaches people to tick without reading, and all four fail together
   * anyway.
   *
   * Location and photographs are separate and default to OFF. They are
   * genuinely optional: a shop with no GPS pin still gets its delivery, and
   * shelf photographs are merchandising intelligence rather than a
   * precondition of selling anyone a crate of water. Defaulting them on would
   * make the choice theatre.
   */
  const [agreed, setAgreed] = useState(false);
  const [allowLocation, setAllowLocation] = useState(false);
  const [allowPhotos, setAllowPhotos] = useState(false);

  const valid = agreed
    && legalName.trim().length >= 2
    && shopName.trim().length >= 2
    && address1.trim().length >= 3;

  async function submit() {
    setError(''); setFieldErrors({}); setBusy(true);
    try {
      await api.post('/api/customers/register', {
        legalName: legalName.trim(),
        tradeName: tradeName.trim() || undefined,
        pocName: pocName.trim() || undefined,
        phone,
        registrationTicket: ticket,
        altPhone: altPhone.replace(/\D/g, '') || undefined,
        // Presence of a GSTIN decides tax invoice vs bill of supply.
        gstin: gstin.trim().toUpperCase() || undefined,
        fssaiNumber: fssai.trim() || undefined,
        salespersonCode: salespersonCode.trim().toUpperCase() || undefined,
        shops: [{
          shopName: shopName.trim(),
          storeTypeId: storeTypeId || undefined,
          addressLine1: address1.trim(),
          landmark: landmark.trim() || undefined,
          areaId: areaId || undefined,
          pincode: pincode.replace(/\D/g, '') || undefined,
          latitude: coords?.latitude,
          longitude: coords?.longitude,
          locationAccuracyM: coords?.accuracyM,
          openTime: open24 ? undefined : openTime,
          closeTime: open24 ? undefined : closeTime,
          isOpen24h: open24,
          weeklyOffDay: weeklyOff === '' ? undefined : Number(weeklyOff),
          isPrimary: true,
        }],
        consents: {
          termsOfService: agreed,
          privacyPolicy: agreed,
          dataProcessing: agreed,
          whatsappCommunication: agreed,
          locationCapture: allowLocation,
          photoCapture: allowPhotos,
        },
      }, { anonymous: true });

      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError(t('app.somethingWrong'));
      }
    } finally {
      setBusy(false);
    }
  }

  // Step 1 — who is this? Asked before any form, because the number alone does
  // not say whether a shop, a salesman or a driver is signing up.
  if (!who) {
    return (
      <div className="app">
        <main className="screen" style={{ maxWidth: 420 }}>
          <div className="stack-4" style={{ paddingTop: 32 }}>
            <div className="stack-2">
              <h1>{t('auth.welcome')}</h1>
              <p className="t-sm t-muted">{phone}</p>
            </div>
            <p className="t-sm t-muted">Tell us who you are, so we show you the right form.</p>
            <Button block size="lg" onClick={() => setWho('shop')}>
              I run a shop / I’m a customer
            </Button>
            <Button block size="lg" variant="ghost" onClick={() => setWho('salesperson')}>
              I’m a salesman
            </Button>
            <Button block size="lg" variant="ghost" onClick={() => setWho('driver')}>
              I’m a driver
            </Button>
            <Button variant="ghost" block onClick={() => navigate('/login', { replace: true })}>
              {t('app.back')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Staff enrolment is a screen of its own; shops fall through to the form below.
  if (who === 'salesperson' || who === 'driver') {
    return (
      <StaffRegister
        role={who}
        phone={phone}
        ticket={ticket}
        onBack={() => setWho('')}
      />
    );
  }

  if (submitted) {
    return (
      <div className="app">
        <main className="screen center" style={{ maxWidth: 420 }}>
          <div className="stack-4 t-center">
            <div
              style={{
                width: 56, height: 56, borderRadius: '50%', background: 'var(--ok-bg)',
                color: 'var(--ok)', display: 'grid', placeItems: 'center', margin: '0 auto',
                fontSize: 26, fontWeight: 700,
              }}
              aria-hidden
            >✓</div>
            <h1>{t('auth.pendingApproval')}</h1>
            <p className="t-sm t-muted">{t('auth.pendingApprovalHint')}</p>
            <Button variant="ghost" block onClick={() => navigate('/login', { replace: true })}>
              {t('app.done')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <main className="screen" style={{ maxWidth: 560 }}>
        <div className="stack-4" style={{ paddingTop: 24 }}>
          <div className="stack-2">
            <h1>{t('auth.welcome')}</h1>
            <p className="t-sm t-muted">{phone}</p>
          </div>

          {/*
            SAY WHAT HAPPENED, BEFORE ASKING FOR A GSTIN.

            Reported 7 Oct: a salesperson whose number had been moved onto
            another account kept being shown this form. He was not a
            prospective shop and had no business details to give; he simply
            had a working number attached to the wrong record, and the app
            answered by asking him to register a company.

            This screen is reached for exactly one reason — the number is not
            on the system — and that sentence is worth more to most people
            who land here than the whole form beneath it. Staff get told who
            to ask; a genuine new shop reads one line and carries on.
          */}
          <Banner tone="info" title="This number is not registered yet">
            <span className="t-sm">
              If you work for the distributor, do not fill this in — ask the office to
              check the mobile number on your staff record. This form is for a shop
              registering with the distributor for the first time.
            </span>
          </Banner>

          {/*
            No ticket means this screen was opened directly rather than
            through a verified code, and the server will refuse the
            submission. Better to say so at the top than after the form.
          */}
          {!ticket && (
            <Banner tone="warn" title="Start from the login screen">
              <span className="t-sm">
                This page needs a freshly verified mobile number. Go back, enter your
                number and the code you receive, and you will be brought straight here.
              </span>
            </Banner>
          )}

          {error && <Banner tone="danger">{error}</Banner>}

          <Card>
            <div className="stack-3" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Business details</h3>
              <TextInput
                label="Business name" required value={legalName}
                error={fieldErrors.legalName}
                onChange={(e) => setLegalName(e.target.value)}
                autoComplete="organization"
              />
              <TextInput label="Trade name" value={tradeName} onChange={(e) => setTradeName(e.target.value)} />
              <TextInput label="Contact person" value={pocName} onChange={(e) => setPocName(e.target.value)} />
              <TextInput
                label="Alternate mobile" inputMode="numeric" value={altPhone}
                onChange={(e) => setAltPhone(phoneInputDigits(e.target.value))}
              />
              <TextInput
                label="GSTIN" value={gstin} maxLength={15}
                error={fieldErrors.gstin}
                hint="Leave blank if you are not GST registered"
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                style={{ textTransform: 'uppercase' }}
              />
              <TextInput label="FSSAI number" value={fssai} onChange={(e) => setFssai(e.target.value)} />
              <TextInput
                label="Salesperson code" value={salespersonCode}
                error={fieldErrors.salespersonCode}
                hint="If a salesperson referred you"
                onChange={(e) => setSalespersonCode(e.target.value.toUpperCase())}
              />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Customer details</h3>
              <TextInput
                label="Shop name" required value={shopName}
                error={fieldErrors.shopName}
                onChange={(e) => setShopName(e.target.value)}
              />
              {storeTypes.length > 0 && (
                <Select
                  label="Customer type" value={storeTypeId} placeholder="Select"
                  options={storeTypes.map((s) => ({ value: s.id, label: s.name_en }))}
                  onChange={(e) => setStoreTypeId(e.target.value)}
                />
              )}
              <TextInput
                label="Address" required value={address1}
                error={fieldErrors.addressLine1}
                onChange={(e) => setAddress1(e.target.value)}
                autoComplete="street-address"
              />
              <TextInput label="Landmark" value={landmark} onChange={(e) => setLandmark(e.target.value)} />
              {areas.length > 0 && (
                <Select
                  label="Area" value={areaId} placeholder="Select"
                  options={areas.map((a) => ({ value: a.id, label: a.name_en }))}
                  onChange={(e) => setAreaId(e.target.value)}
                />
              )}
              <TextInput
                label="Pincode" numeric maxLength={6} value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="postal-code"
              />

              {/* The pin is what makes every later "Directions" link work.
                  Captured once here rather than geocoded, so there is no Maps
                  API cost — ever. */}
              <div className="field">
                <label>Customer location</label>
                {coords ? (
                  <Banner tone="ok">
                    <div className="row" style={{ gap: 8 }}>
                      <IconPin width={16} height={16} />
                      <span className="t-sm">
                        Saved · accurate to about {Math.round(coords.accuracyM)} m
                      </span>
                    </div>
                  </Banner>
                ) : (
                  <Button variant="ghost" block busy={locating} onClick={() => void captureLocation()}>
                    Use my current location
                  </Button>
                )}
                <span className="field__hint">
                  Helps our delivery team find your shop
                </span>
              </div>

              <Switch
                checked={open24} onChange={setOpen24}
                label="Open 24 hours"
              />
              {!open24 && (
                <div className="grid-2">
                  <TextInput
                    label="Opens at" type="time" value={openTime}
                    onChange={(e) => setOpenTime(e.target.value)}
                  />
                  <TextInput
                    label="Closes at" type="time" value={closeTime}
                    onChange={(e) => setCloseTime(e.target.value)}
                  />
                </div>
              )}
              <Select
                label="Weekly off" value={weeklyOff} placeholder="No weekly off"
                options={[
                  { value: '0', label: 'Sunday' }, { value: '1', label: 'Monday' },
                  { value: '2', label: 'Tuesday' }, { value: '3', label: 'Wednesday' },
                  { value: '4', label: 'Thursday' }, { value: '5', label: 'Friday' },
                  { value: '6', label: 'Saturday' },
                ]}
                onChange={(e) => setWeeklyOff(e.target.value)}
              />
            </div>
          </Card>

          {/*
            CONSENT, IMMEDIATELY BEFORE THE BUTTON.

            consent_records has existed since migration 0009 and nothing ever
            wrote to it, so every customer registered so far was created with
            no record of what they agreed to. Consent cannot be backdated, so
            those cannot be fixed — only stopped from growing.

            The mandatory sentence is one tick, not four: four separate
            required boxes trains people to tick without reading, and all four
            fail together in any case. The two optional ones are separate and
            OFF by default, because a default-on optional consent is not a
            choice.
          */}
          <Card className="stack-2">
            <Switch
              checked={agreed}
              onChange={setAgreed}
              label="I agree to the terms of service and the privacy policy"
              hint="Covers using your details to take orders, raise invoices and keep your ledger,
                    and sending your login code by WhatsApp — the code is how you sign in, so this
                    one is needed to have an account at all."
            />
            <hr className="divider" />
            <div className="t-xs t-muted">
              These two are optional. You will be served either way.
            </div>
            <Switch
              checked={allowLocation}
              onChange={setAllowLocation}
              label="Save my location"
              hint="Helps plan the delivery round. Without it you are still delivered to."
            />
            <Switch
              checked={allowPhotos}
              onChange={setAllowPhotos}
              label="Allow photographs of my premises and shelves"
              hint="Used to record stock displays. Declining changes nothing about your orders."
            />
          </Card>

          <Banner tone="info">
            Your registration goes to our team for approval. You can browse and
            order once it is approved.
          </Banner>

          <Button block size="lg" busy={busy} disabled={!valid} onClick={() => void submit()}>
            {t('app.submit')}
          </Button>
        </div>
      </main>
    </div>
  );
}
